import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Alert, Typography } from 'antd';
import {
  MailOutlined,
  GoogleOutlined,
  AppleOutlined,
  LockOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthLayout from '../../layouts/AuthLayout';
import logo from '../../assets/logo-login.png';
import useAuth from '../../hooks/useAuth';
import { motion, AnimatePresence } from 'framer-motion';


const { Text } = Typography;

export default function ShopLoginPage() {
  const [mode, setMode] = useState('options'); // options | email
  const [loading, setLoading] = useState(false);
  const [globalError, setGlobalError] = useState(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const { loginWithEmail, loginWithGoogle, loginWithApple, isAuthenticated, loading: authLoading } = useAuth();

  const cardStyle = {
    width: '100%',
    maxWidth: 420,
    borderRadius: 20,
    boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
    background: '#0f1115',
  };

  const primaryButtonStyle = {
    height: 48,
    borderRadius: 24,
    fontWeight: 600,
  };

  const handleEmailLogin = async (values) => {
    setLoading(true);
    setGlobalError(null);
    try {
        await loginWithEmail({
            email: values.email,
            password: values.password,
            remember: true,
        });
      navigate(from ? from.pathname + (from.search || '') : '/', { replace: true });
    } catch (err) {
      console.error('Login error:', err); 
            console.log('Error details:', {
                status: err?.response?.status,
                data: err?.response?.data,
                code: err?.response?.data?.code,
                message: err?.response?.data?.message
            });
            
            const status = err?.response?.status;
            const data = err?.response?.data;
            const errorCode = data?.code || err?.code || data.error?.code;
            const errorMessage = data?.message || data.error?.message || err?.message || 'Error desconocido';

            form.setFields([]);

            // Manejar errores específicos por código
            if (errorCode === 'INVALID_CREDENTIALS') {
                setGlobalError('Email o contraseña incorrectos. Verifica tus credenciales e inténtalo de nuevo.');
            } else if (status === 400 || status === 401 || status === 422) {
                // Errores de validación del servidor
                const fieldErrors = data?.fieldErrors || {};
                const fields = Object.entries(fieldErrors).map(([name, errors]) => ({
                    name,
                    errors: Array.isArray(errors) ? errors : [String(errors || 'Error')],
                }));

                if (fields.length) {
                    form.setFields(fields);
                } else {
                    setGlobalError(errorMessage === 'Unknown error' ? 'Credenciales incorrectas. Verifica tu email y contraseña.' : errorMessage);
                }
            } else if (status === 403) {
                setGlobalError('Tu cuenta no tiene permisos para acceder. Contacta al administrador.');
            } else if (status === 429) {
                setGlobalError('Demasiados intentos de inicio de sesión. Espera unos minutos antes de intentar de nuevo.');
            } else if (status >= 500) {
                setGlobalError('Error del servidor. Por favor, inténtalo más tarde.');
            } else if (err.code === 'NETWORK_ERROR' || err.code === 'VALIDATION_ERROR' || !navigator.onLine) {
                setGlobalError('Sin conexión a internet. Verifica tu conexión e inténtalo de nuevo.');
            } else {
                // Si el mensaje es "Unknown error", mostrar un mensaje más amigable
                const friendlyMessage = errorMessage === 'Unknown error' 
                    ? 'Ha ocurrido un error inesperado. Por favor, inténtalo de nuevo.' 
                    : errorMessage;
                setGlobalError(friendlyMessage);
            }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
        if (!authLoading && isAuthenticated) {
            navigate(from ? from.pathname + (from.search || '') : '/', { replace: true });
        }
    }, [authLoading, isAuthenticated, navigate, from]);

  const onLoginWithGoogle = async () => {
    setLoading(true);
    setGlobalError(null);

    try {
        if (from) {
          const returnTo = from.pathname + (from.search || '');
          console.log('[Login] Guardando checkout_returnTo:', returnTo);
          sessionStorage.setItem('checkout_returnTo', returnTo);
        }
        await loginWithGoogle();
    } catch (err) {
        console.error('Login error:', err); 
        console.log('Error details:', {
            status: err?.response?.status,
            data: err?.response?.data,
            code: err?.response?.data?.code,
            message: err?.response?.data?.message
        });
        
        const status = err?.response?.status;
        const data = err?.response?.data;
        const errorCode = data?.code || err?.code;
        const errorMessage = data?.message || err?.message || 'Error desconocido';

        form.setFields([]);

        // Manejar errores específicos por código
        if (errorCode === 'INVALID_CREDENTIALS') {
            setGlobalError('Email o contraseña incorrectos. Verifica tus credenciales e inténtalo de nuevo.');
        } else if (status === 400 || status === 401 || status === 422) {
            // Errores de validación del servidor
            const fieldErrors = data?.fieldErrors || {};
            const fields = Object.entries(fieldErrors).map(([name, errors]) => ({
                name,
                errors: Array.isArray(errors) ? errors : [String(errors || 'Error')],
            }));

            if (fields.length) {
                form.setFields(fields);
            } else {
                setGlobalError(errorMessage === 'Unknown error' ? 'Credenciales incorrectas. Verifica tu email y contraseña.' : errorMessage);
            }
        } else if (status === 403) {
            setGlobalError('Tu cuenta no tiene permisos para acceder. Contacta al administrador.');
        } else if (status === 429) {
            setGlobalError('Demasiados intentos de inicio de sesión. Espera unos minutos antes de intentar de nuevo.');
        } else if (status >= 500) {
            setGlobalError('Error del servidor. Por favor, inténtalo más tarde.');
        } else if (err.code === 'NETWORK_ERROR' || err.code === 'VALIDATION_ERROR' || !navigator.onLine) {
            setGlobalError('Sin conexión a internet. Verifica tu conexión e inténtalo de nuevo.');
        } else {
            // Si el mensaje es "Unknown error", mostrar un mensaje más amigable
            const friendlyMessage = errorMessage === 'Unknown error' 
                ? 'Ha ocurrido un error inesperado. Por favor, inténtalo de nuevo.' 
                : errorMessage;
            setGlobalError(friendlyMessage);
        }
    } finally {
        setLoading(false);
    }
};

  const onLoginWithApple = async () => {
    setLoading(true);
    setGlobalError(null);

    try {
        if (from) {
          const returnTo = from.pathname + (from.search || '');
          sessionStorage.setItem('checkout_returnTo', returnTo);
        }
        await loginWithApple();
    } catch (err) {
        console.error('Login error:', err);

        const status = err?.response?.status;
        const data = err?.response?.data;
        const errorCode = data?.code || err?.code;
        const errorMessage = data?.message || err?.message || 'Error desconocido';

        form.setFields([]);

        if (errorCode === 'INVALID_CREDENTIALS') {
            setGlobalError('Email o contraseña incorrectos. Verifica tus credenciales e inténtalo de nuevo.');
        } else if (status === 400 || status === 401 || status === 422) {
            const fieldErrors = data?.fieldErrors || {};
            const fields = Object.entries(fieldErrors).map(([name, errors]) => ({
                name,
                errors: Array.isArray(errors) ? errors : [String(errors || 'Error')],
            }));

            if (fields.length) {
                form.setFields(fields);
            } else {
                setGlobalError(errorMessage === 'Unknown error' ? 'Credenciales incorrectas. Verifica tu email y contraseña.' : errorMessage);
            }
        } else if (status === 403) {
            setGlobalError('Tu cuenta no tiene permisos para acceder. Contacta al administrador.');
        } else if (status === 429) {
            setGlobalError('Demasiados intentos de inicio de sesión. Espera unos minutos antes de intentar de nuevo.');
        } else if (status >= 500) {
            setGlobalError('Error del servidor. Por favor, inténtalo más tarde.');
        } else if (err.code === 'NETWORK_ERROR' || err.code === 'VALIDATION_ERROR' || !navigator.onLine) {
            setGlobalError('Sin conexión a internet. Verifica tu conexión e inténtalo de nuevo.');
        } else {
            const friendlyMessage = errorMessage === 'Unknown error'
                ? 'Ha ocurrido un error inesperado. Por favor, inténtalo de nuevo.'
                : errorMessage;
            setGlobalError(friendlyMessage);
        }
    } finally {
        setLoading(false);
    }
};

  return (
    <AuthLayout>
      <Card style={cardStyle} bodyStyle={{ padding: 32 }}>
        <img
          src={logo}
          alt="Shop"
          style={{ width: 140, margin: '0 auto 16px', display: 'block' }}
        />

        <Text
          style={{
            display: 'block',
            textAlign: 'center',
            marginBottom: 24,
            color: '#bfbfbf',
          }}
        >
          Registro / Inicio de sesión
        </Text>

        {globalError && (
          <Alert
            type="error"
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ marginBottom: 16 }}
            message={globalError}
            closable
            onClose={() => setGlobalError(null)}
          />
        )}

        <AnimatePresence mode="wait">
            {mode === 'options' && (
                <motion.div
                key="options"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                >
                <Button
                    type="primary"
                    icon={<MailOutlined />}
                    block
                    style={{
                    ...primaryButtonStyle,
                    background: '#22c55e',
                    border: 'none',
                    marginBottom: 12,
                    }}
                    onClick={() => setMode('email')}
                >
                    Conectar con correo
                </Button>

                <Button
                    icon={<GoogleOutlined />}
                    block
                    style={{
                    ...primaryButtonStyle,
                    marginBottom: 12,
                    }}
                    onClick={onLoginWithGoogle}
                >
                    Continuar con Google
                </Button>

                <Button
                    icon={<AppleOutlined />}
                    block
                    style={primaryButtonStyle}
                    onClick={onLoginWithApple}
                >
                    Conectar con Apple
                </Button>
                </motion.div>
            )}

            {mode === 'email' && (
                <motion.div
                key="email"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                >
                <Form
                    form={form}
                    layout="vertical"
                    requiredMark={false}
                    onFinish={handleEmailLogin}
                >
                    <Form.Item
                    name="email"
                    rules={[
                        { required: true, message: 'Ingresa tu email' },
                        { type: 'email', message: 'Email inválido' },
                    ]}
                    >
                    <Input
                        size="large"
                        placeholder="tu@email.com"
                        prefix={<MailOutlined />}
                    />
                    </Form.Item>

                    <Form.Item
                    name="password"
                    rules={[
                        { required: true, message: 'Ingresa tu contraseña' },
                    ]}
                    >
                    <Input.Password
                        size="large"
                        placeholder="Contraseña"
                        prefix={<LockOutlined />}
                    />
                    </Form.Item>

                    <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={loading}
                    style={primaryButtonStyle}
                    >
                    Continuar
                    </Button>

                    <Button
                    type="link"
                    block
                    style={{ marginTop: 8 }}
                    onClick={() => setMode('options')}
                    >
                    ← Volver
                    </Button>
                </Form>
                </motion.div>
            )}
        </AnimatePresence>

      </Card>
    </AuthLayout>
  );
}
